import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../services/apiClient';
import { LoadingScreen } from '../common/LoadingScreen';

interface FeatureGuardProps {
  flag: string;
  children: React.ReactNode;
}

interface UserWithFlags {
  roles: string[];
  feature_flags: string[];
}

/**
 * Feature flag authorization guard.
 * Grants access if the user is a SUPER_ADMIN or has the specified feature flag.
 */
export const FeatureGuard: React.FC<FeatureGuardProps> = ({ flag, children }) => {
  const { isAuthenticated, isAuthEnabled } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthEnabled) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      if (!isAuthenticated) {
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiClient.get<UserWithFlags>('/auth/user');
        const userRoles = response.roles || [];
        const flags = response.feature_flags || [];
        setHasAccess(userRoles.includes('SUPER_ADMIN') || flags.includes(flag));
      } catch (err) {
        console.error('Failed to check access:', err);
        setError('Failed to verify access permissions');
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [isAuthenticated, isAuthEnabled, flag]);

  if (isLoading) {
    return <LoadingScreen message="Verifying access permissions..." />;
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          Access Denied. You do not have permission to view this page.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please contact your administrator if you believe you should have access.
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
